/**
 * Company identity — the single source of truth for the public-facing company
 * details shown on the login page, in the Hub footer, and in the structured
 * data emitted by index.html and the legal-page generator.
 *
 * Why this lives on the pre-auth surface: OAuth reviewers (Google brand
 * verification, Microsoft Entra publisher verification), browser reputation
 * services and client IT teams only ever see the login page and the legal
 * pages. Everything that establishes Jones + Palmer as a real, long-established
 * UK business has to be visible there, or it may as well not exist.
 *
 * It is also a statutory requirement: s.82 Companies Act 2006 and the Company,
 * Limited Liability Partnership and Business (Names and Trading Disclosures)
 * Regulations 2015 require a UK company's websites to state the company name,
 * registered number, place of registration and registered office address.
 *
 * Keep these values character-identical to the ones on jonesandpalmer.co.uk,
 * LinkedIn and the Companies House register — entity-resolution systems match
 * organisations across sources by exact string, so drift here weakens exactly
 * the signal the page is trying to send.
 */

export const COMPANY = {
  /** Preferred public name. See CLAUDE.md §3 for name usage. */
  name: 'Jones + Palmer',
  /** Registered name, as it appears on the Companies House register. */
  legalName: 'Jones and Palmer Limited',
  companyNumber: '00239324',
  placeOfRegistration: 'England and Wales',

  /**
   * The business has traded in Birmingham since 1906; Jones and Palmer Limited
   * was incorporated on 6 May 1929. Both are stated so the 120-year claim
   * reconciles with the Companies House record rather than contradicting it.
   */
  foundedYear: 1906,
  incorporatedDate: '1929-05-06',

  /** Registered office, exactly as recorded at Companies House. */
  registeredOffice: '87 Carver Street, Birmingham, B1 3AL, United Kingdom',
  /** Trading address, as published on jonesandpalmer.co.uk. */
  address: {
    street: 'Floor 1, Media Works, 87 Carver Street',
    locality: 'Birmingham',
    postcode: 'B1 3AL',
    country: 'GB',
  },

  phone: '+44 121 236 9007',
  phoneHref: 'tel:+441212369007',
  email: 'data@jonesandpalmer.co.uk',
  icoRegistration: 'Z2473618',

  url: 'https://www.jonesandpalmer.co.uk',
  contactUrl: 'https://www.jonesandpalmer.co.uk/talk-to-us',
  companiesHouseUrl: 'https://find-and-update.company-information.service.gov.uk/company/00239324',
  linkedInUrl: 'https://www.linkedin.com/company/jones-and-palmer/',
  instagramUrl: 'https://www.instagram.com/jonesandpalmer/',
} as const;

/**
 * Certifications and memberships, shown on the public login page. ISO 27001 and
 * Cyber Essentials lead deliberately — they are the two a security reviewer
 * actually weights when deciding whether this domain should hold their data.
 */
export const ACCREDITATIONS = [
  'ISO 27001',
  'ISO 9001',
  'Cyber Essentials',
  'Investors in People',
  'IR Society member',
  'FSC certified',
] as const;

/** Whole years trading, so the copy never goes stale. 1906 → 120 in 2026. */
export function yearsTrading(now: Date = new Date()): number {
  return now.getFullYear() - COMPANY.foundedYear;
}

/**
 * The statutory disclosure line. Kept as one function so the login page, the
 * Hub and the generated legal pages cannot drift apart.
 */
export function registeredDetailsLine(): string {
  return (
    `${COMPANY.legalName} · Registered in ${COMPANY.placeOfRegistration}, ` +
    `company number ${COMPANY.companyNumber} · Registered office: ${COMPANY.registeredOffice}`
  );
}
